/**
 * Product SEO Visibility Panel
 *
 * Story 56: Track Product SEO Visibility in Google Search
 *
 * Shows Google ranking positions for a product across:
 * - Brand search (exact product name)
 * - Product searches (AI-generated product-specific queries)
 * - Question search (top demand question for category)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Available markets for SERP tracking
const SERP_MARKETS = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
];
import {
  Search,
  TrendingUp,
  TrendingDown,
  Minus,
  ExternalLink,
  Loader2,
  Sparkles,
  HelpCircle,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  BarChart3,
  Globe,
} from 'lucide-react';
import {
  checkProductVisibility,
  enableProductTracking,
  disableProductTracking,
  VisibilityReport,
} from '@/services/productVisibilityService';
import { useToast } from '@/components/ui/use-toast';

interface VisibilityPanelProps {
  productId: string;
  productName: string;
  productUrl: string;
  siteId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrackingChange?: (tracking: boolean) => void;
}

function PositionBadge({ position }: { position: number | null }) {
  if (position === null) {
    return (
      <Badge variant="destructive" className="font-mono">
        <XCircle className="h-3 w-3 mr-1" />
        Not found
      </Badge>
    );
  }

  if (position <= 3) {
    return (
      <Badge className="bg-green-600 font-mono">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        #{position}
      </Badge>
    );
  }

  if (position <= 10) {
    return (
      <Badge className="bg-green-500 font-mono">
        #{position}
      </Badge>
    );
  }

  if (position <= 20) {
    return (
      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 font-mono">
        <AlertTriangle className="h-3 w-3 mr-1" />
        #{position}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="font-mono text-muted-foreground">
      #{position}
    </Badge>
  );
}

export function VisibilityPanel({
  productId,
  productName,
  productUrl,
  siteId,
  open,
  onOpenChange,
  onTrackingChange,
}: VisibilityPanelProps) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [visibility, setVisibility] = useState<VisibilityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState('US');
  const { toast } = useToast();

  useEffect(() => {
    if (open && productId) {
      loadVisibility();
    }
  }, [open, productId]);

  // Reload when market changes
  const handleMarketChange = (market: string) => {
    setSelectedMarket(market);
    loadVisibility(true, market);
  };

  const loadVisibility = async (forceRefresh = false, market?: string) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const data = await checkProductVisibility(productId, siteId, forceRefresh, market || selectedMarket);
      setVisibility(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load visibility data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleTrackingToggle = async (enabled: boolean) => {
    try {
      setTrackingLoading(true);

      if (enabled) {
        await enableProductTracking(productId, siteId);
        toast({
          title: 'Tracking enabled',
          description: 'This product will be checked daily for ranking changes.',
        });
      } else {
        await disableProductTracking(productId);
        toast({
          title: 'Tracking disabled',
          description: 'Daily tracking has been stopped. Historical data is preserved.',
        });
      }

      // Reload to get updated tracking status
      await loadVisibility();
      onTrackingChange?.(enabled);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update tracking',
        variant: 'destructive',
      });
    } finally {
      setTrackingLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0">
        <div className="p-6 pr-12 overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 shrink-0" />
              SEO Visibility
            </DialogTitle>
            <div className="flex items-center gap-2 mt-1">
              <DialogDescription className="truncate flex-1 text-sm" title={productName}>
                {productName}
              </DialogDescription>
              <Select value={selectedMarket} onValueChange={handleMarketChange} disabled={loading || refreshing}>
                <SelectTrigger className="w-[120px] h-7 text-xs shrink-0">
                  <Globe className="h-3 w-3 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERP_MARKETS.map((market) => (
                    <SelectItem key={market.code} value={market.code}>
                      <span className="flex items-center gap-2">
                        <span>{market.flag}</span>
                        <span>{market.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </DialogHeader>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : visibility ? (
          <div className="space-y-6 relative overflow-hidden">
            {/* Loading overlay when switching markets */}
            {refreshing && (
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-lg">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                <p className="text-sm text-muted-foreground">
                  Loading rankings for {SERP_MARKETS.find(m => m.code === selectedMarket)?.name || selectedMarket}...
                </p>
              </div>
            )}
            {/* Brand Search Section */}
            <div className="border rounded-lg p-4 overflow-hidden">
              <div className="flex items-center justify-between mb-3 gap-2">
                <h3 className="font-medium flex items-center gap-2 min-w-0">
                  <TrendingUp className="h-4 w-4 text-blue-500 shrink-0" />
                  <span className="shrink-0">Brand Search</span>
                  <span className="text-xs text-muted-foreground font-normal shrink-0">(exact product name)</span>
                </h3>
                <div className="shrink-0">
                  <PositionBadge position={visibility.brandSearch.position} />
                </div>
              </div>
              <div className="bg-muted/50 rounded p-3 mb-2 overflow-hidden">
                <div className="flex items-center justify-between gap-3 min-w-0">
                  <p
                    className="text-sm truncate flex-1 min-w-0"
                    title={visibility.brandSearch.query}
                  >
                    {visibility.brandSearch.query}
                  </p>
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(visibility.brandSearch.query)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 shrink-0 p-1"
                    title="Search on Google"
                  >
                    <Search className="h-4 w-4" />
                  </a>
                </div>
              </div>
              {visibility.brandSearch.matchedUrl && (
                <a
                  href={visibility.brandSearch.matchedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1 max-w-full"
                  title={visibility.brandSearch.matchedUrl}
                >
                  <ExternalLink className="h-3 w-3 shrink-0" />
                  <span className="truncate">{new URL(visibility.brandSearch.matchedUrl).pathname}</span>
                </a>
              )}
            </div>

            {/* Product Search Terms Section */}
            <div className="border rounded-lg p-4 overflow-hidden">
              <div className="flex items-center justify-between mb-2 gap-2">
                <h3 className="font-medium flex items-center gap-2 shrink-0">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  Product Searches
                </h3>
                <Badge variant="outline" className="text-xs shrink-0">
                  AI Generated
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                How users might search for this specific product
              </p>
              <div className="space-y-2">
                {visibility.researchTerms.map((term, index) => (
                  <div key={index} className="flex items-center justify-between py-1 border-b last:border-b-0 gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-sm truncate" title={term.term}>
                        {term.term}
                      </span>
                      <a
                        href={`https://www.google.com/search?q=${encodeURIComponent(term.term)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 shrink-0"
                        title="Verify on Google"
                      >
                        <Search className="h-3 w-3" />
                      </a>
                    </div>
                    <div className="shrink-0">
                      <PositionBadge position={term.position} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Question Search Section */}
            {visibility.questionSearch && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 text-orange-500" />
                    Top Question
                  </h3>
                  <PositionBadge position={visibility.questionSearch.position} />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm text-muted-foreground flex-1 truncate" title={visibility.questionSearch.query}>
                    "{visibility.questionSearch.query}"
                  </p>
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(visibility.questionSearch.query)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 shrink-0"
                    title="Verify on Google"
                  >
                    <Search className="h-3 w-3" />
                  </a>
                </div>
                {visibility.questionSearch.hasFeaturedSnippet && (
                  <Alert className="mt-2 bg-amber-50 border-amber-200">
                    <Sparkles className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      Featured snippet available!
                      {visibility.questionSearch.featuredSnippetHolder && (
                        <span className="block text-xs mt-1">
                          Currently held by: {visibility.questionSearch.featuredSnippetHolder}
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Tracking Controls */}
            <div className="border rounded-lg p-4 bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch
                    id="tracking"
                    checked={visibility.trackingEnabled}
                    onCheckedChange={handleTrackingToggle}
                    disabled={trackingLoading}
                  />
                  <Label htmlFor="tracking" className="font-medium">
                    Track Daily
                  </Label>
                  {trackingLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadVisibility(true)}
                    disabled={refreshing}
                  >
                    {refreshing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    <span className="ml-2">Refresh</span>
                  </Button>
                  {visibility.trackingEnabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Navigate to dashboard with this product selected
                        window.location.href = `/seo/visibility?product=${productId}`;
                      }}
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      View History
                    </Button>
                  )}
                </div>
              </div>
              {visibility.trackingEnabled && (
                <p className="text-xs text-muted-foreground mt-2">
                  Last checked: {new Date(visibility.lastChecked).toLocaleString()}
                </p>
              )}
              {!visibility.trackingEnabled && (
                <p className="text-xs text-muted-foreground mt-2">
                  Enable tracking to monitor ranking changes over time (~$0.05/day)
                </p>
              )}
            </div>
          </div>
        ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default VisibilityPanel;
